import { useCallback, useEffect, useRef, useState } from 'react';

import saveFile from 'helpers/saveFile';
import { Toast } from 'primereact/toast';

import { AttachmentService } from 'components/services/AttachmentService';
import { TaskService } from 'components/services/TaskService';

const MAX_FILES = 10;
const MAX_SIZE_MB = 5;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

export interface Comment {
  id: number;
  text: string;
  createdAt: Date;
  userName: string;
  taskId: number;
  device: string;
  isRead: boolean;
  attachments?: Array<{
    id: number;
    name: string;
    compressedFormFile?: {
      fileContents: string;
      contentType: string;
    };
  }>;
}

interface UseTaskCommentsProps {
  taskId: number;
  setHasNewComments: (value: boolean) => void;
  setHasComments: (value: boolean) => void;
}

export const useTaskComments = ({ taskId, setHasNewComments, setHasComments }: UseTaskCommentsProps) => {
  const toast = useRef<Toast>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editFileInputRef = useRef<HTMLInputElement>(null);

  const [comments, setComments] = useState<Comment[]>([]);
  const [commentText, setCommentText] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<Array<{id: string, file: File, preview?: string}>>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [deletingCommentId, setDeletingCommentId] = useState<number | null>(null);

  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editCommentText, setEditCommentText] = useState<string>('');
  const [editPendingFiles, setEditPendingFiles] = useState<Array<{id: string, file: File, preview?: string}>>([]);
  const [editExistingAttachments, setEditExistingAttachments] = useState<Array<{id: number, name: string}>>([]);
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState<number[]>([]);
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    if (!loading && comments.length > 0) {
      setHasComments(true);
    } else if (!loading) {
      setHasComments(false);
    }
  }, [comments, loading]);

  const getCommentsByTask = useCallback(async () => {
    setLoading(true);
    try {
      const result = await TaskService.getTaskComments(taskId);
      
      if (result.data && result.data.isSuccess && result.data.value) {
        const itemsList = result.data.value.itemsList || [];
        const formattedComments: Comment[] = itemsList.map((item: any) => ({
          id: item.id,
          text: item.text,
          createdAt: new Date(item.datetime),
          userName: item.userName,
          taskId: item.taskId,
          isRead: item.isRead,
          device: item.device,
          attachments: item.attachments || []
        }));
        
        const sortedComments = formattedComments.sort((a, b) => 
          b.createdAt.getTime() - a.createdAt.getTime()
        );
        
        setComments(sortedComments);

        const hasUnread = sortedComments.some(c => !c.isRead);
        setHasNewComments(hasUnread);
        
        if (hasUnread) {
          const unreadIds = sortedComments.filter(c => !c.isRead).map(c => c.id);
          await TaskService.markCommentsAsRead(unreadIds);
          setComments(prev => prev.map(c => 
            unreadIds.includes(c.id) ? { ...c, isRead: true } : c
          ));
        }
      }
    } catch (error) {
      console.log('Ошибка при загрузке комментариев:', error);
    } finally {
      setLoading(false);
    }
  }, [taskId, setHasNewComments]);

  useEffect(() => {
    getCommentsByTask();
  }, [taskId, getCommentsByTask]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    const errors: string[] = [];
    const validFiles: typeof pendingFiles = [];

    if (pendingFiles.length + selectedFiles.length > MAX_FILES) {
      const error = `Можно прикрепить не более ${MAX_FILES} файлов.`;
      errors.push(error);
      toast.current?.show({ severity: 'error', summary: 'Ошибка', detail: error, life: 5000 });
    }

    selectedFiles.forEach(file => {
      if (file.size > MAX_SIZE_BYTES) {
        const error = `Файл "${file.name}" превышает ${MAX_SIZE_MB}MB`;
        errors.push(error);
        toast.current?.show({ severity: 'error', summary: 'Ошибка', detail: error, life: 5000 });
        return;
      }

      const allowedExtensions = ['.png', '.jpg', '.jpeg', '.xls', '.xlsx', '.doc', '.docx', '.pdf', '.pptx'];
      const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!allowedExtensions.includes(fileExt)) {
        const error = `Файл "${file.name}" имеет неподдерживаемый формат`;
        errors.push(error);
        toast.current?.show({ severity: 'error', summary: 'Ошибка', detail: error, life: 5000 });
        return;
      }

      if (pendingFiles.some(f => f.file.name === file.name)) {
        const error = `Файл "${file.name}" уже добавлен`;
        errors.push(error);
        toast.current?.show({ severity: 'error', summary: 'Ошибка', detail: error, life: 5000 });
        return;
      }

      let preview = undefined;
      if (file.type.startsWith('image/')) {
        preview = URL.createObjectURL(file);
      }

      validFiles.push({
        id: `${Date.now()}-${Math.random()}`,
        file,
        preview
      });
    });

    setValidationErrors(errors);
    setPendingFiles(prev => [...prev, ...validFiles]);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (id: string) => {
    const fileToRemove = pendingFiles.find(f => f.id === id);
    if (fileToRemove?.preview) {
      URL.revokeObjectURL(fileToRemove.preview);
    }
    setPendingFiles(prev => prev.filter(f => f.id !== id));
  };

  const handleSendComment = async () => {
    const trimmedText = commentText.trim();
    
    if (trimmedText.length === 0 && pendingFiles.length === 0) {
      setError('Нельзя отправить пустой комментарий');
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    setError('');
    setSending(true);
    
    try {
      const filesToSend = pendingFiles.map(f => f.file);
      
      const result = await TaskService.addTaskComment(
        trimmedText || '(без текста)',
        taskId,
        'web',
        filesToSend
      );
      
      if (result.data.isSuccess) {
        pendingFiles.forEach(f => {
          if (f.preview) URL.revokeObjectURL(f.preview);
        });
        setPendingFiles([]);
        setCommentText('');
        await getCommentsByTask();
        
        toast.current?.show({
          severity: 'success',
          summary: 'Успешно',
          detail: 'Комментарий отправлен',
          life: 3000
        });
      } else {
        if (!result.data.isSuccess) {
          toast.current?.show({
            severity: 'error',
            summary: 'Ошибка',
            detail: result.data.error,
            life: 3000
          });
        }
      }
    } catch (error) {
      console.error('Ошибка при отправке комментария:', error);
      setError('Ошибка при отправке комментария');
      setTimeout(() => setError(''), 3000);
    } finally {
      setSending(false);
    }
  };

  const handleEditComment = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditCommentText(comment.text);
    setEditExistingAttachments(comment.attachments?.map(a => ({ id: a.id, name: a.name })) || []);
    setEditPendingFiles([]);
    setDeletedAttachmentIds([]);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditCommentText('');
    setEditPendingFiles([]);
    setEditExistingAttachments([]);
    setDeletedAttachmentIds([]);
  };

  const handleEditFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    const validFiles: typeof editPendingFiles = [];

    if (editPendingFiles.length + editExistingAttachments.length + selectedFiles.length > MAX_FILES) {
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка',
        detail: `Можно прикрепить не более ${MAX_FILES} файлов.`,
        life: 5000
      });
      return;
    }

    selectedFiles.forEach(file => {
      if (file.size > MAX_SIZE_BYTES) {
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка',
          detail: `Файл "${file.name}" превышает ${MAX_SIZE_MB}MB`,
          life: 5000
        });
        return;
      }

      const allowedExtensions = ['.png', '.jpg', '.jpeg', '.xls', '.xlsx', '.doc', '.docx', '.pdf', '.pptx'];
      const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!allowedExtensions.includes(fileExt)) {
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка',
          detail: `Файл "${file.name}" имеет неподдерживаемый формат`,
          life: 5000
        });
        return;
      }

      if (editPendingFiles.some(f => f.file.name === file.name) ||
          editExistingAttachments.some(a => a.name === file.name)) {
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка',
          detail: `Файл "${file.name}" уже добавлен`,
          life: 5000
        });
        return;
      }

      let preview = undefined;
      if (file.type.startsWith('image/')) {
        preview = URL.createObjectURL(file);
      }

      validFiles.push({
        id: `${Date.now()}-${Math.random()}`,
        file,
        preview
      });
    });

    setEditPendingFiles(prev => [...prev, ...validFiles]);
    if (editFileInputRef.current) {
      editFileInputRef.current.value = '';
    }
  };

  const handleSaveEditComment = async (commentId: number) => {
    if (!editCommentText.trim() && editPendingFiles.length === 0) {
      toast.current?.show({ severity: 'error', summary: 'Ошибка', detail: 'Нельзя сохранить пустой комментарий', life: 3000 });
      return;
    }

    setSavingEdit(true);
    try {
      const result = await TaskService.updateTaskComment(
        commentId,
        editCommentText.trim() || '(без текста)',
        taskId,
        'web',
        editPendingFiles.map(f => f.file),
        deletedAttachmentIds
      );

      if (result.data.isSuccess) {
        toast.current?.show({ severity: 'success', summary: 'Успешно', detail: 'Комментарий обновлен', life: 3000 });
        handleCancelEdit();
        await getCommentsByTask();
      }
      else {
        if (!result.data.isSuccess) {
          toast.current?.show({
            severity: 'error',
            summary: 'Ошибка',
            detail: result.data.error,
            life: 3000
          });
        }
      }
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось обновить комментарий', life: 3000 });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteComment = async (commentId: number, userName: string) => {
    if (!window.confirm(`Вы уверены, что хотите удалить комментарий пользователя ${userName}?`)) {
      return;
    }

    setDeletingCommentId(commentId);
    try {
      await TaskService.deleteTaskComment(commentId);
      
      setComments(prev => prev.filter(c => c.id !== commentId));
      
      toast.current?.show({
        severity: 'success',
        summary: 'Успешно',
        detail: 'Комментарий удален',
        life: 3000
      });
      
      const updatedComments = comments.filter(c => c.id !== commentId);
      const hasUnread = updatedComments.some(c => !c.isRead);
      setHasNewComments(hasUnread);
      
    } catch (error) {
      console.error('Ошибка при удалении комментария:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Не удалось удалить комментарий',
        life: 3000
      });
    } finally {
      setDeletingCommentId(null);
    }
  };

  const handleAttachmentDownload = async (attachment: any) => {
    try {
      const result = await AttachmentService.get(attachment.id);
      await saveFile(result.data, attachment.name, '');
      
      toast.current?.show({
        severity: 'success',
        summary: 'Успешно',
        detail: 'Файл скачан',
        life: 2000
      });
    } catch (error) {
      console.error('Ошибка при скачивании файла:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Не удалось скачать файл',
        life: 3000
      });
    }
  };

  const handleAttachmentDoubleClick = async (attachment: any) => {
    const imageFormats = ['png', 'jpg', 'jpeg', 'gif', 'svg'];
    const fileExtension = attachment.name?.split('.').pop()?.toLowerCase();

    if (imageFormats.includes(fileExtension || '')) {
      const result = await AttachmentService.get(attachment.id);
      let blob;

      if (fileExtension === 'svg') {
        blob = new Blob([result.data], { type: 'image/svg+xml' });
      } else {
        blob = new Blob([result.data], { type: `image/${fileExtension}` });
      }

      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } else {
      await handleAttachmentDownload(attachment);
    }
  };

  const getAttachmentPreview = (attachment: any): string | null => {
    if (attachment.compressedFormFile?.fileContents) {
      return `data:${attachment.compressedFormFile.contentType};base64,${attachment.compressedFormFile.fileContents}`;
    }
    return null;
  };

  const getDeviceIcon = (device: string) => {
    if (device?.toLowerCase().includes('mobile')) {
      return 'pi pi-mobile';
    }
    return 'pi pi-desktop';
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return {
    toast,
    fileInputRef,
    editFileInputRef,
    
    comments,
    commentText,
    setCommentText,
    error,
    setError, 
    loading,
    sending,
    pendingFiles,
    validationErrors,
    deletingCommentId,
    editingCommentId,
    editCommentText,
    setEditCommentText,
    editPendingFiles,
    setEditPendingFiles, 
    editExistingAttachments,
    setEditExistingAttachments, 
    deletedAttachmentIds,
    setDeletedAttachmentIds, 
    savingEdit,
    
    getCommentsByTask,
    handleFileSelect,
    handleRemoveFile,
    handleSendComment,
    handleEditComment,
    handleCancelEdit,
    handleEditFileSelect,
    handleSaveEditComment,
    handleDeleteComment,
    handleAttachmentDownload,
    handleAttachmentDoubleClick,
    getAttachmentPreview,
    getDeviceIcon,
    formatDate,
    
    MAX_FILES,
    MAX_SIZE_MB,
  };
};